import { zodResolver } from '@hookform/resolvers/zod'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useIsMobile,
} from '@racefront/shadcn-ui'
import { AvatarCropper } from '@racefront/common'
import dayjs from 'dayjs'
import { Loader2, User, Edit2 } from 'lucide-react'
import { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import * as z from 'zod'
import { UserAccount } from '../../../types/user'
import { DateInput } from './date-input'
import { useUserSchema } from './schema'
import LocaleSelect from './locale-select'
import { useLocalization } from '@racefront/common/context/localization-context'
import { MobileSectionEditDialog } from './mobile-section-edit-dialog'
import { FALLBACK_PHONE } from './constants'

type UserFormProps = {
  user?: Partial<UserAccount>
  isLoading?: boolean
  onSave?: (user: Partial<UserAccount>) => void
}

export type UserFormRef = {
  triggerValidation: () => void
  reset: () => void
}

export const UserForm = forwardRef<UserFormRef, UserFormProps>(function UserForm({ user, onSave, isLoading }, ref) {
  const { countries } = useLocalization()
  const isMobile = useIsMobile()

  const intl = useIntl()
  const userSchema = useUserSchema()

  type FormUser = z.infer<typeof userSchema>
  const form = useForm<FormUser>({
    resolver: zodResolver(userSchema),
    mode: 'onChange',
    defaultValues: {
      ...user,
      role: Array.isArray(user?.role) ? user.role[0] : user?.role,
    },
  })

  const { reset, trigger, getValues, setValue, watch, control, formState } = form

  // Expose triggerValidation method via ref
  useImperativeHandle(ref, () => ({
    triggerValidation: () => {
      trigger()
    },
    reset: () => {
      reset({
        ...user,
        role: Array.isArray(user?.role) ? user.role[0] : user?.role,
      })
    },
  }))

  // Reset form when user data changes (e.g. after async fetch)
  useEffect(() => {
    if (user) {
      reset({
        ...user,
        role: Array.isArray(user?.role) ? user.role[0] : user?.role,
      })
    }
  }, [user, reset])

  const { dirtyFields } = formState

  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [phoneModalOpen, setPhoneModalOpen] = useState(false)
  const [pendingEmail, setPendingEmail] = useState('')
  const [pendingPhone, setPendingPhone] = useState('')

  // Mobile edit dialog state
  const [mobileEditSection, setMobileEditSection] = useState<
    'email' | 'phone' | 'locale' | 'personal' | 'address' | null
  >(null)
  const [isMobileDialogOpen, setIsMobileDialogOpen] = useState(false)

  const phoneFieldRef = useRef<HTMLDivElement>(null)

  const isPhonePlaceholder = user?.phoneNumber === FALLBACK_PHONE
  const isEmailUnverified = !!user?.email && !user?.emailVerified
  const isPhoneUnverified = !!user?.phoneNumber && !isPhonePlaceholder && !user?.phoneVerified

  const handleVerifyEmailNow = () => {
    onSave?.({ email: user?.email })
  }

  const handleVerifyPhoneNow = () => {
    onSave?.({ phoneNumber: user?.phoneNumber })
  }

  const handleUpdatePhoneNow = () => {
    phoneFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    phoneFieldRef.current?.querySelector('input')?.focus()
  }

  const handleAvatarCrop = (_file: File, dataUrl: string) => {
    setValue('picture', dataUrl)
    onSave?.({ picture: dataUrl })
  }

  const handleEmailSave = async () => {
    const isValid = await trigger('email')
    if (!isValid) return
    const email = getValues('email')
    setPendingEmail(email)
    setEmailModalOpen(true)
  }

  const confirmEmailSave = () => {
    onSave?.({ email: pendingEmail })
    setEmailModalOpen(false)
    setValue('email', user?.email || '', { shouldDirty: false })
  }

  const handlePhoneSave = async () => {
    const isValid = await trigger('phoneNumber')
    if (!isValid) return
    const phone = getValues('phoneNumber')
    setPendingPhone(phone)
    setPhoneModalOpen(true)
  }

  const confirmPhoneSave = () => {
    onSave?.({ phoneNumber: pendingPhone })
    setPhoneModalOpen(false)
    setValue('phoneNumber', user?.phoneNumber || '', { shouldDirty: false })
  }

  const handleSaveField = async (fieldName: keyof FormUser) => {
    const isValid = await trigger(fieldName)
    if (!isValid) return
    const fieldValue = getValues(fieldName)
    onSave?.({ [fieldName]: fieldValue })
  }

  const handleSavePersonalInfo = async () => {
    const isValid = await trigger(['firstname', 'lastname', 'birthdate', 'gender', 'nationality'])
    if (!isValid) return
    const personalData = {
      firstname: getValues('firstname'),
      lastname: getValues('lastname'),
      birthdate: getValues('birthdate'),
      gender: getValues('gender') || undefined,
      nationality: getValues('nationality') || undefined,
    }
    onSave?.(personalData)
  }

  const handleSaveAddress = async () => {
    const isValid = await trigger(['street', 'city', 'postCode', 'country'])
    if (!isValid) return
    const addressData = {
      street: getValues('street') || undefined,
      city: getValues('city') || undefined,
      postCode: getValues('postCode') || undefined,
      country: getValues('country') || undefined,
    }
    onSave?.(addressData)
  }

  // Mobile handlers
  const handleMobileEdit = (section: 'email' | 'phone' | 'locale' | 'personal' | 'address') => {
    setMobileEditSection(section)
    setIsMobileDialogOpen(true)
  }

  const handleMobileSave = async () => {
    if (!mobileEditSection) return

    switch (mobileEditSection) {
      case 'email':
        await handleEmailSave()
        break
      case 'phone':
        await handlePhoneSave()
        break
      case 'locale':
        await handleSaveField('locale')
        break
      case 'personal':
        await handleSavePersonalInfo()
        break
      case 'address':
        await handleSaveAddress()
        break
    }
  }

  const closeMobileDialog = () => {
    setIsMobileDialogOpen(false)
    setMobileEditSection(null)
  }

  // Helper to get formatted value for display
  const getDisplayValue = (value: string | undefined) => {
    if (!value || value === '' || value === '0') return '-'
    return value
  }

  const getGenderDisplay = (gender: string | undefined) => {
    if (!gender) return intl.formatMessage({ id: 'form.gender.not_known', defaultMessage: 'Not Known' })
    switch (gender) {
      case '1':
        return intl.formatMessage({ id: 'form.gender.male', defaultMessage: 'Male' })
      case '2':
        return intl.formatMessage({ id: 'form.gender.female', defaultMessage: 'Female' })
      case '3':
        return intl.formatMessage({ id: 'form.gender.not_applicable', defaultMessage: 'Not Applicable' })
      default:
        return intl.formatMessage({ id: 'form.gender.not_known', defaultMessage: 'Not Known' })
    }
  }

  const getCountryName = (code: string | undefined) => {
    if (!code) return '-'
    return countries?.find((c) => c.code === code)?.name || code
  }

  // Render read-only section for mobile
  const renderMobileReadOnlySection = (
    title: string,
    description: string,
    fields: { label: string; value: string; badge?: React.ReactNode }[],
    onEdit: () => void,
  ) => (
    <div className="bg-card/25 border-border rounded-xl border p-6 shadow-sm">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-card-foreground text-lg font-semibold">{title}</h3>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={onEdit} className="ml-2 shrink-0">
          <Edit2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="space-y-3">
        {fields.map((field, index) => (
          <div key={index} className="flex flex-wrap items-center justify-between gap-1 border-b pb-2 last:border-b-0">
            <span className="text-muted-foreground text-sm">{field.label}</span>
            <span className="flex items-center gap-2">
              <span className="text-card-foreground text-sm font-medium">{field.value}</span>
              {field.badge}
            </span>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <Form {...form}>
      <div className="space-y-6">
        {/* Avatar */}
        <div className="bg-card/25 border-border rounded-xl border p-6 shadow-sm">
          <div className="flex justify-between gap-6">
            <div className="flex flex-col">
              <h3 className="text-card-foreground text-lg font-semibold">
                <FormattedMessage id="form.section.avatar" defaultMessage="Profile Avatar" />
              </h3>
              <p className="text-muted-foreground text-sm">
                <FormattedMessage
                  id="form.section.avatar.description"
                  defaultMessage="Upload a profile picture to personalize your account."
                />
              </p>
              <p className="text-muted-foreground mt-2 text-xs">
                <FormattedMessage id="form.avatar.hint" defaultMessage="JPG, PNG or GIF. Max size 2MB." />
              </p>
            </div>
            <FormField
              control={control}
              name="picture"
              render={() => (
                <FormItem>
                  <AvatarCropper
                    currentAvatar={user?.picture}
                    alt={user?.firstname || 'User'}
                    size={96}
                    outputSize={512}
                    onCrop={handleAvatarCrop}
                    fallback={
                      user?.firstname?.[0]?.toUpperCase() ||
                      user?.lastname?.[0]?.toUpperCase() || <User className="h-12 w-12" />
                    }
                    labels={{
                      clickToUpload: intl.formatMessage({ id: 'form.avatar.click', defaultMessage: 'Click to upload' }),
                      cropImage: intl.formatMessage({ id: 'form.avatar.cropImage', defaultMessage: 'Crop Image' }),
                      adjustImage: intl.formatMessage({
                        id: 'form.avatar.adjustImage',
                        defaultMessage: 'Adjust the image to fit the desired area.',
                      }),
                      cancel: intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' }),
                      save: intl.formatMessage({ id: 'common.save', defaultMessage: 'Save' }),
                    }}
                    disabled={isLoading}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Email - Mobile Read-Only or Desktop Editable */}
        {isMobile ? (
          renderMobileReadOnlySection(
            intl.formatMessage({ id: 'form.section.email', defaultMessage: 'Email Address' }),
            intl.formatMessage({
              id: 'form.section.email.description',
              defaultMessage: 'Your email address will be used for account recovery and important notifications.',
            }),
            [
              {
                label: intl.formatMessage({ id: 'form.email_label', defaultMessage: 'Email' }),
                value: getDisplayValue(watch('email')),
                badge: isEmailUnverified && (
                  <Badge variant="outline" className="border-amber-500 text-amber-500">
                    <FormattedMessage id="form.email.unverified_badge" defaultMessage="Unverified" />
                  </Badge>
                ),
              },
            ],
            () => handleMobileEdit('email'),
          )
        ) : (
          <div className="bg-card/25 border-border rounded-xl border p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <h3 className="text-card-foreground text-lg font-semibold">
                <FormattedMessage id="form.section.email" defaultMessage="Email Address" />
              </h3>
              {isEmailUnverified && (
                <Badge variant="outline" className="border-amber-500 text-amber-500">
                  <FormattedMessage id="form.email.unverified_badge" defaultMessage="Unverified" />
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              <FormattedMessage
                id="form.section.email.description"
                defaultMessage="Your email address will be used for account recovery and important notifications."
              />
            </p>
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.email_label" defaultMessage="Email" />
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder={intl.formatMessage({
                            id: 'form.email_placeholder',
                            defaultMessage: 'your@email.com',
                          })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {isEmailUnverified && (
                <p className="text-sm text-amber-600">
                  <FormattedMessage
                    id="form.email.unverified_prompt"
                    defaultMessage="Your email address isn't verified yet. Please verify it to keep receiving important notifications."
                  />
                </p>
              )}
              <div className="flex justify-end gap-2">
                {isEmailUnverified && (
                  <Button
                    type="button"
                    onClick={handleVerifyEmailNow}
                    disabled={isLoading}
                    size="sm"
                    variant="outline"
                    className="border-amber-500 text-amber-600"
                  >
                    <FormattedMessage id="form.email.verify_now" defaultMessage="Verify now" />
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={handleEmailSave}
                  disabled={isLoading || !dirtyFields.email}
                  size="sm"
                  variant="outline"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <FormattedMessage id="common.save" defaultMessage="Save" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Phone - Mobile Read-Only or Desktop Editable */}
        {isMobile ? (
          renderMobileReadOnlySection(
            intl.formatMessage({ id: 'form.section.phone', defaultMessage: 'Phone Number' }),
            intl.formatMessage({
              id: 'form.section.phone.description',
              defaultMessage: 'Your phone number for account verification and contact purposes.',
            }),
            [
              {
                label: intl.formatMessage({ id: 'form.phoneNumber_label', defaultMessage: 'Phone Number' }),
                value: getDisplayValue(watch('phoneNumber')),
                badge: isPhonePlaceholder ? (
                  <Badge variant="outline" className="border-destructive text-destructive">
                    <FormattedMessage id="form.phone.missing_badge" defaultMessage="Missing" />
                  </Badge>
                ) : (
                  isPhoneUnverified && (
                    <Badge variant="outline" className="border-amber-500 text-amber-500">
                      <FormattedMessage id="form.phone.unverified_badge" defaultMessage="Unverified" />
                    </Badge>
                  )
                ),
              },
            ],
            () => handleMobileEdit('phone'),
          )
        ) : (
          <div className="bg-card/25 border-border rounded-xl border p-6 shadow-sm" ref={phoneFieldRef}>
            <div className="mb-4 flex items-center gap-2">
              <h3 className="text-card-foreground text-lg font-semibold">
                <FormattedMessage id="form.section.phone" defaultMessage="Phone Number" />
              </h3>
              {isPhonePlaceholder ? (
                <Badge variant="outline" className="border-destructive text-destructive">
                  <FormattedMessage id="form.phone.missing_badge" defaultMessage="Missing" />
                </Badge>
              ) : (
                isPhoneUnverified && (
                  <Badge variant="outline" className="border-amber-500 text-amber-500">
                    <FormattedMessage id="form.phone.unverified_badge" defaultMessage="Unverified" />
                  </Badge>
                )
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              <FormattedMessage
                id="form.section.phone.description"
                defaultMessage="Your phone number for account verification and contact purposes."
              />
            </p>
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.phoneNumber_label" defaultMessage="Phone Number" />
                      </FormLabel>
                      <FormControl>
                        <PhoneInput
                          {...field}
                          placeholder={intl.formatMessage({
                            id: 'form.phoneNumber_placeholder',
                            defaultMessage: '+1234567890',
                          })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {isPhonePlaceholder && (
                <p className="text-destructive text-sm">
                  <FormattedMessage
                    id="form.phone.missing_prompt"
                    defaultMessage="You haven't set a phone number yet. Please update it now."
                  />
                </p>
              )}
              {isPhoneUnverified && (
                <p className="text-sm text-amber-600">
                  <FormattedMessage
                    id="form.phone.unverified_prompt"
                    defaultMessage="Your phone number isn't verified yet. Please verify it to receive SMS notifications."
                  />
                </p>
              )}
              <div className="flex justify-end gap-2">
                {isPhonePlaceholder && (
                  <Button
                    type="button"
                    onClick={handleUpdatePhoneNow}
                    disabled={isLoading}
                    size="sm"
                    variant="outline"
                    className="border-destructive text-destructive"
                  >
                    <FormattedMessage id="form.phone.update_now" defaultMessage="Update now" />
                  </Button>
                )}
                {isPhoneUnverified && (
                  <Button
                    type="button"
                    onClick={handleVerifyPhoneNow}
                    disabled={isLoading}
                    size="sm"
                    variant="outline"
                    className="border-amber-500 text-amber-600"
                  >
                    <FormattedMessage id="form.phone.verify_now" defaultMessage="Verify now" />
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={handlePhoneSave}
                  disabled={isLoading || !dirtyFields.phoneNumber}
                  size="sm"
                  variant="outline"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <FormattedMessage id="common.save" defaultMessage="Save" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Locale/Language - Mobile Read-Only or Desktop Editable */}
        {isMobile ? (
          renderMobileReadOnlySection(
            intl.formatMessage({ id: 'form.section.language', defaultMessage: 'Language Preference' }),
            intl.formatMessage({
              id: 'form.section.language.description',
              defaultMessage: 'Choose your preferred language for the application interface.',
            }),
            [
              {
                label: intl.formatMessage({ id: 'form.locale_label', defaultMessage: 'Language' }),
                value: getDisplayValue(watch('locale')),
              },
            ],
            () => handleMobileEdit('locale'),
          )
        ) : (
          <div className="bg-card/25 border-border rounded-xl border p-6 shadow-sm">
            <div className="mb-4">
              <h3 className="text-card-foreground text-lg font-semibold">
                <FormattedMessage id="form.section.language" defaultMessage="Language Preference" />
              </h3>
              <p className="text-muted-foreground text-sm">
                <FormattedMessage
                  id="form.section.language.description"
                  defaultMessage="Choose your preferred language for the application interface."
                />
              </p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={control}
                  name="locale"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.locale_label" defaultMessage="Language" />
                      </FormLabel>
                      <LocaleSelect field={field} />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={() => handleSaveField('locale')}
                  disabled={isLoading || !dirtyFields.locale}
                  size="sm"
                  variant="outline"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <FormattedMessage id="common.save" defaultMessage="Save" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Personal Information - Mobile Read-Only or Desktop Editable */}
        {isMobile ? (
          renderMobileReadOnlySection(
            intl.formatMessage({ id: 'form.section.personal_info', defaultMessage: 'Personal Information' }),
            intl.formatMessage({
              id: 'form.section.personal_info.description',
              defaultMessage: 'Your personal details including name, birth date, gender, and nationality.',
            }),
            [
              {
                label: intl.formatMessage({ id: 'form.firstname_label', defaultMessage: 'First Name' }),
                value: getDisplayValue(watch('firstname')),
              },
              {
                label: intl.formatMessage({ id: 'form.lastname_label', defaultMessage: 'Last Name' }),
                value: getDisplayValue(watch('lastname')),
              },
              {
                label: intl.formatMessage({ id: 'form.gender_label', defaultMessage: 'Gender' }),
                value: getGenderDisplay(watch('gender') || ''),
              },
              {
                label: intl.formatMessage({ id: 'form.birthdate_label', defaultMessage: 'Birth Date' }),
                value: getDisplayValue(watch('birthdate')),
              },
              {
                label: intl.formatMessage({ id: 'form.nationality_label', defaultMessage: 'Nationality' }),
                value: getCountryName(watch('nationality')),
              },
            ],
            () => handleMobileEdit('personal'),
          )
        ) : (
          <div className="bg-card/25 border-border rounded-xl border p-6 shadow-sm">
            <div className="mb-4">
              <h3 className="text-card-foreground text-lg font-semibold">
                <FormattedMessage id="form.section.personal_info" defaultMessage="Personal Information" />
              </h3>
              <p className="text-muted-foreground text-sm">
                <FormattedMessage
                  id="form.section.personal_info.description"
                  defaultMessage="Your personal details including name, birth date, gender, and nationality."
                />
              </p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={control}
                  name="firstname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.firstname_label" defaultMessage="First Name" />
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={intl.formatMessage({ id: 'form.firstname_placeholder', defaultMessage: 'John' })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="lastname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.lastname_label" defaultMessage="Last Name" />
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={intl.formatMessage({ id: 'form.lastname_placeholder', defaultMessage: 'Doe' })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.gender_label" defaultMessage="Gender" />
                      </FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={intl.formatMessage({
                                id: 'form.gender_placeholder',
                                defaultMessage: 'Select gender',
                              })}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="0">
                            <FormattedMessage id="form.gender.not_known" defaultMessage="Not Known" />
                          </SelectItem>
                          <SelectItem value="1">
                            <FormattedMessage id="form.gender.male" defaultMessage="Male" />
                          </SelectItem>
                          <SelectItem value="2">
                            <FormattedMessage id="form.gender.female" defaultMessage="Female" />
                          </SelectItem>
                          <SelectItem value="3">
                            <FormattedMessage id="form.gender.not_applicable" defaultMessage="Not Applicable" />
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="birthdate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.birthdate_label" defaultMessage="Birth Date" />
                      </FormLabel>
                      <DateInput
                        field={field}
                        isRequired
                        formater={(date) => dayjs(date)?.format('YYYY-MM-DD')}
                        placeholder={<FormattedMessage id="form.birthdate_placeholder" defaultMessage="Pick a date" />}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={control}
                  name="nationality"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.nationality_label" defaultMessage="Nationality" />
                      </FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={intl.formatMessage({
                                id: 'form.nationality_placeholder',
                                defaultMessage: 'Select nationality',
                              })}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {countries?.map(({ code, name }) => (
                            <SelectItem key={code} value={code}>
                              {name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={handleSavePersonalInfo}
                  disabled={
                    isLoading ||
                    (!dirtyFields.gender &&
                      !dirtyFields.birthdate &&
                      !dirtyFields.nationality &&
                      !dirtyFields.firstname &&
                      !dirtyFields.lastname)
                  }
                  size="sm"
                  variant="outline"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <FormattedMessage id="common.save" defaultMessage="Save" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Address Information - Mobile Read-Only or Desktop Editable */}
        {isMobile ? (
          renderMobileReadOnlySection(
            intl.formatMessage({ id: 'form.section.address_info', defaultMessage: 'Address Information' }),
            intl.formatMessage({
              id: 'form.section.address_info.description',
              defaultMessage: 'Your residential address for billing and delivery purposes.',
            }),
            [
              {
                label: intl.formatMessage({ id: 'form.street_label', defaultMessage: 'Street' }),
                value: getDisplayValue(watch('street')),
              },
              {
                label: intl.formatMessage({ id: 'form.city_label', defaultMessage: 'City' }),
                value: getDisplayValue(watch('city')),
              },
              {
                label: intl.formatMessage({ id: 'form.postCode_label', defaultMessage: 'Post Code' }),
                value: getDisplayValue(watch('postCode')),
              },
              {
                label: intl.formatMessage({ id: 'form.country_label', defaultMessage: 'Country' }),
                value: getCountryName(watch('country')),
              },
            ],
            () => handleMobileEdit('address'),
          )
        ) : (
          <div className="bg-card/25 border-border rounded-xl border p-6 shadow-sm">
            <div className="mb-4">
              <h3 className="text-card-foreground text-lg font-semibold">
                <FormattedMessage id="form.section.address_info" defaultMessage="Address Information" />
              </h3>
              <p className="text-muted-foreground text-sm">
                <FormattedMessage
                  id="form.section.address_info.description"
                  defaultMessage="Your residential address for billing and delivery purposes."
                />
              </p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={control}
                  name="street"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.street_label" defaultMessage="Street" />
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={intl.formatMessage({
                            id: 'form.street_placeholder',
                            defaultMessage: '123 Main St',
                          })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.city_label" defaultMessage="City" />
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={intl.formatMessage({ id: 'form.city_placeholder', defaultMessage: 'New York' })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={control}
                  name="postCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.postCode_label" defaultMessage="Post Code" />
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={intl.formatMessage({ id: 'form.postCode_placeholder', defaultMessage: '10001' })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <FormattedMessage id="form.country_label" defaultMessage="Country" />
                      </FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={intl.formatMessage({
                                id: 'form.country_placeholder',
                                defaultMessage: 'Select country',
                              })}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {countries?.map(({ code, name }) => (
                            <SelectItem key={code} value={code}>
                              {name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={handleSaveAddress}
                  disabled={
                    isLoading ||
                    (!dirtyFields.street && !dirtyFields.city && !dirtyFields.postCode && !dirtyFields.country)
                  }
                  size="sm"
                  variant="outline"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <FormattedMessage id="common.save" defaultMessage="Save" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Email Confirmation Modal */}
        <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <FormattedMessage id="form.email.confirm.title" defaultMessage="Update Email Address" />
              </DialogTitle>
              <DialogDescription>
                <FormattedMessage
                  id="form.email.confirm.description"
                  defaultMessage="You are about to change your email address to {email}. You will need to verify this new email address before you can use it for login."
                  values={{ email: <strong>{pendingEmail}</strong> }}
                />
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailModalOpen(false)}>
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              <Button onClick={confirmEmailSave}>
                <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phone Confirmation Modal */}
        <Dialog open={phoneModalOpen} onOpenChange={setPhoneModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <FormattedMessage id="form.phone.confirm.title" defaultMessage="Update Phone Number" />
              </DialogTitle>
              <DialogDescription>
                <FormattedMessage
                  id="form.phone.confirm.description"
                  defaultMessage="You are about to change your phone number to {phone}. You will need to verify this new phone number to receive SMS notifications."
                  values={{ phone: <strong>{pendingPhone}</strong> }}
                />
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPhoneModalOpen(false)}>
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              <Button onClick={confirmPhoneSave}>
                <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mobile Section Edit Dialog */}
        {mobileEditSection && (
          <MobileSectionEditDialog
            isOpen={isMobileDialogOpen}
            onClose={closeMobileDialog}
            section={mobileEditSection}
            form={form}
            onSave={handleMobileSave}
            isLoading={isLoading || false}
          />
        )}
      </div>
    </Form>
  )
})
